import React, { useState, useEffect, useCallback, useRef } from "react";

import { useRealtimeConversation, getRealtimeEngine, getRealtimeEndpoint, getCustomModel, getVoiceName } from '../hooks/useRealtimeConversation';
import { useSharedState } from '../hooks/SharedStateContext';
import { usePlanner } from '../hooks/PlannerContext';
import { useTranslation } from 'react-i18next';
import { useEndConversation } from '../hooks/endConversation';
import { useTimer } from '../hooks/TimerContext';
import { useConnectionCircuitBreaker } from '../hooks/useConnectionCircuitBreaker';

import { _config } from '../Constants';
import { createConversationDoc, updateConversationInFirestore, getFirestoreDocId, updateFeedbackInFirestore, getCurrentUserId, getLearningActivityDoc, getUserMemory, getCurrentUserName, getUserWork, auth } from '../utilities/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUrlParameter } from '../utilities/urlUtils';
import { getTargetLanguageText } from '../utilities/targetLanguageTranslations';
import { sanitizeData } from '../utilities/utils'
import { withTimeout, TimeoutError } from '../utilities/promiseUtils'
import { trackConversationSendMessage, trackConversationStarted, trackFeedbackSubmitted, trackRealtimeStartStuckRecovered, trackMicrophoneDeadRecovered } from '../utilities/tracking'
import useRealtimeStartWatchdog from '../hooks/useRealtimeStartWatchdog';
import * as Sentry from '@sentry/react';

import { getMainStartActivity, getFreeformStartActivity } from '../utilities/activitySelection';
import { useLiveActivityProgress } from '../hooks/useLiveActivityProgress';
import { isLearningActivity, mergeConversationProgress } from '../utilities/conversationProgress';
import { COMPLETION_SIGNAL, isCompletionResponse } from '../utilities/signals';
import ActivityList from './Activities/ActivityList';
import ActivitySessionProgressBar from './Activities/ActivitySessionProgressBar';

import ConversationComponent from './ConversationComponent';
import MicrophonePermissionModal from './Modal/MicrophonePermissionModal';
import ConversationFeedbackModal from './Modal/ConversationFeedbackModal';

import axios from 'axios';

import toast from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad, faComments, faVolumeHigh } from '@fortawesome/free-solid-svg-icons';

import './Conversation.css'; // Small overrides
import { getActivityDuration } from '../utilities/activities.ts';

const isRealtimeCompletionMessage = (message) => {
  if (isCompletionResponse(message)) {
    return true;
  }

  if (typeof message !== 'string') {
    return false;
  }

  if (message.includes(COMPLETION_SIGNAL)) {
    return true;
  }

  try {
    return isCompletionResponse(JSON.parse(message));
  } catch {
    return false;
  }
};

const getLearningPathActivity = (selectedActivity) => (
  typeof selectedActivity === 'string' && selectedActivity.startsWith('learning-path-')
    ? selectedActivity
    : null
);

/**
 * Creating the conversation doc reads the learner's memory and name before it writes, so
 * it is three round trips to Firestore. On a phone that is online but passing no data the
 * SDK queues them rather than failing, and the await never returns — leaving the start
 * sequence latched on `loading` with nothing to catch. Generous for a real slow
 * connection, and still well inside the start watchdog's budget.
 */
const CONVERSATION_DOC_TIMEOUT_MS = 15000;

const Conversation = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState(''); // New state for error message
  const [conversationId, setConversationId] = useState(null); // New state for conversationId
  const [activityDocAtStart, setActivityDocAtStart] = useState(null);

  const { sharedState, setSharedState, handleActivities, updateSelectedActivity, getLatestLevel, setConversationStartHandler } = useSharedState(); 
  const plannerContext = usePlanner();
  const { currentPlannerData, checkPlannerDataOrPromptGoal } = plannerContext || {};
  const [isPlannerLoading, setIsPlannerLoading] = useState(false);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingActivity, setPendingActivity] = useState(null);
  const [showConversationFeedbackModal, setShowConversationFeedbackModal] = useState(false);
  const lastConversationIdRef = useRef(null);
  const lastActivityRef = useRef(null);

  // Refs to preserve conversation data during navigation/unmount
  const chatHistoryRef = useRef([]);
  const conversationIdRef = useRef(null);
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connection attempts
  const conversationStartTimeRef = useRef(null); // Track when conversation started to prevent premature finishing
  const { startActivityTimer } = useTimer(); // Get startTimer from TimerContext
  const { t } = useTranslation();

  // Circuit breaker to prevent infinite reconnection loops
  const circuitBreaker = useConnectionCircuitBreaker({
    maxAttempts: 5,           // Max 5 connection attempts
    timeWindow: 10000,        // Within 10 seconds
    cooldownPeriod: 30000,    // Block for 30 seconds if triggered
    onCircuitOpen: ({ attempts, cooldownPeriod }) => {
      setErrorMessage(
        `Too many connection attempts (${attempts}). Please wait ${cooldownPeriod / 1000} seconds before trying again. If this persists, please refresh the page.`
      );
    },
    onCircuitReset: () => {
      setErrorMessage(''); // Clear error message when circuit resets
    },
  });

  const URL = _config.url;
  const realtimeEngine = getRealtimeEngine();
  const HTTP_agent = `${URL}${getRealtimeEndpoint()}`; 

  const getActiveLearningPathActivity = useCallback(() => (
    getLearningPathActivity(sharedState.selectedActivity || localStorage.getItem('selectedActivity'))
  ), [sharedState.selectedActivity]);

  const applyConversationProgress = useCallback((value) => {
    const activity = sharedState.selectedActivity || localStorage.getItem('selectedActivity');
    if (value && !isLearningActivity(activity)) return;
    setSharedState(prev => ({
      ...prev,
      conversationProgress: value
        ? mergeConversationProgress(prev.conversationProgress, value)
        : null,
    }));
  }, [setSharedState, sharedState.selectedActivity]);

  const readActivityDocAtStart = useCallback(async (selectedActivity) => {
    setActivityDocAtStart(null);
    const activityMatch = typeof selectedActivity === 'string'
      ? selectedActivity.match(/^learning-path-(\d+)$/)
      : null;
    const plannerId = currentPlannerData?.plannerId;
    if (!activityMatch || !plannerId) return null;

    const docData = await getLearningActivityDoc(`${plannerId}_${activityMatch[1]}`);
    setActivityDocAtStart(docData);
    return docData;
  }, [currentPlannerData?.plannerId]);

  const {
    attemptProgressIndex,
    startAttempt,
    onUserTurnCompleted,
    reportCompletion,
  } = useLiveActivityProgress(getActiveLearningPathActivity, applyConversationProgress);

  /** PERMISSION HANDLING */
  const handlePermissionGranted = () => {
    setShowPermissionModal(false);
    if (pendingActivity) {
      proceedWithConversation(pendingActivity);
      setPendingActivity(null);
    }
  };

  const handlePermissionDenied = () => {
    setShowPermissionModal(false);
    setPendingActivity(null);
    // Optionnel: revenir au menu des activités
    setSharedState(prevState => ({
      ...prevState,
      showActivities: true,
      showStartButton: true
    }));
  };

  const proceedWithConversation = async (selectedActivity) => {
    try {
      // From here until the agent reports itself live the learner sees the activity
      // loader and a disabled mic. `armStartWatchdog` is declared further down, once the
      // conversation object it recovers exists; it is initialised well before any click
      // can reach this handler.
      armStartWatchdog();

      // Set initial state (without record: true to avoid race condition)
      setSharedState(prevState => ({
        ...prevState,
        firstUserInput: true,
        startTime: Date.now(),
        showActivities: false, // Hide activities when conversation starts
        showStartButton: false, // Hide start button when conversation starts
        loading: true, // Add a loader while we wait for the realtime
      }));

      // Create a new Firestore document for the conversation FIRST
      // Prepare data with default values or omit undefined fields
      const conversationData = sanitizeData({
        userLevel: sharedState.userLevel ?? 3, // Default to 3 if null
        userAge: sharedState.userAge || 'Adult',
        userLanguage: sharedState.userLanguage || 'en',
        selectedActivity: selectedActivity,
        targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
      });

      // Create a new Firestore document for the conversation.
      // Bounded: a stalled write here used to latch the loader with nothing to catch.
      await withTimeout(
        createConversationDoc(conversationData),
        CONVERSATION_DOC_TIMEOUT_MS,
        'createConversationDoc'
      );

      // One historical read per activity session. All subsequent highlighting
      // is derived from the active conversation in local state.
      await readActivityDocAtStart(selectedActivity);

      // Track after the firestoreDocId is set so the event includes a real conversationId
      trackConversationStarted(true, selectedActivity);

      // NOW trigger the conversation start after firestoreDocId is set
      setSharedState(prevState => ({
        ...prevState,
        record: true // Trigger the startConversation via useEffect
      }));

      // Begin a fresh attempt: bar at 0, start event recorded server-side
      await startAttempt(selectedActivity);

      const time = getActivityDuration(selectedActivity)
      console.log(`Time ${time} for activity ${selectedActivity}`);

      if (time > 0) {
        startActivityTimer(time, () => {
          console.log("Timer has ended");
          // You can add any additional logic here if needed
        });
      }

      // Note: startConversation() is automatically triggered by the useEffect
      // when record: true is set above (line 124). No need to call it directly here.

      // Any other logic to handle when the conversation starts
    } catch (error) {
      console.error('Error creating conversation document:', error);
      setSharedState(prevState => ({
        ...prevState,
        firstUserInput: true,
        startTime: Date.now(),
        showActivities: false, // Hide activities when conversation starts
        showStartButton: true, // Hide start button when conversation starts
        loading: false,
      }));

      if (error instanceof TimeoutError) {
        Sentry.captureException(error, {
          level: 'error',
          tags: { function: 'proceedWithConversation', operation: error.operation },
          extra: { timeoutMs: error.timeoutMs, activityId: selectedActivity },
        });
      }

      // Show error to user. A stall is a connection problem, not a failure the raw
      // message would describe usefully.
      const errorMessage = error instanceof TimeoutError
        ? t('activity_start_timeout')
        : (error?.message || 'Failed to create conversation. Please try again.');
      toast(errorMessage);
    }
  };

  /** CONVERSATION STATE */
  const handleCommencerClick = async () => {
    let activity = getMainStartActivity(sharedState.selectedActivity, currentPlannerData);

    // If no activity returned (no planner data), prompt user to create learning goal
    if (!activity && (!currentPlannerData || currentPlannerData.number_activities === 0)) {
      if (checkPlannerDataOrPromptGoal) {
        setIsPlannerLoading(true);
        const hasPlannerData = await checkPlannerDataOrPromptGoal();
        setIsPlannerLoading(false);

        if (hasPlannerData) {
          // User created a learning goal - get the first activity
          activity = getMainStartActivity(null, currentPlannerData);
        } else {
          // User declined to create a learning goal - start free-form conversation
          activity = null;
        }
      } else {
        // No planner context available (lite mode) - start free-form conversation
        activity = null;
      }
    }

    // Proceed with conversation start (either learning-path activity or free-form null)
    handleConversationStarted(activity);
  };

  const handleConversationStarted = useCallback(async (activity) => {
    if (activity !== sharedState.selectedActivity) {
      updateSelectedActivity(activity, false);
    }

    const selectedActivity = activity || sharedState.selectedActivity;
    console.log("handleConversationStarted", selectedActivity);

    // Check microphone permission first
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: sharedState.echoCancellation,
          noiseSuppression: sharedState.noiseSuppression,
          autoGainControl: sharedState.autoGainControl
        }
      });
      // Permission granted, proceed
      proceedWithConversation(selectedActivity);
    } catch (error) {
      // Permission denied, show modal
      setPendingActivity(selectedActivity);
      setShowPermissionModal(true);
    }
  }, [sharedState, updateSelectedActivity, setPendingActivity, setShowPermissionModal]);

  // Register this component's conversation start handler
  useEffect(() => {
    // console.log('[Conversation] Registering conversation start handler', handleConversationStarted)
    setConversationStartHandler(handleConversationStarted)
    return () => setConversationStartHandler(null)
  }, [setConversationStartHandler, handleConversationStarted])

  // Function to be triggered when isSpeaking changes
  const handleSpeakingChange = (isSpeaking) => {
    if (isSpeaking) {
      console.log('Agent started speaking');
    } else {
      console.log('Agent stopped speaking');
    }

    setSharedState(prevState => ({
      ...prevState,
      talking: isSpeaking,
    }));
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Call endConversation when the tab is closed/navigated away
      // Use refs to ensure we have the latest data even during unmount
      const currentChatHistory = chatHistoryRef.current && chatHistoryRef.current.length > 0
        ? chatHistoryRef.current
        : sharedState.chatHistory;
      const currentConversationId = conversationIdRef.current || conversationId;

      useEndConversationRef.current({ resetState: false, conversation: currentChatHistory, elevenLabsConversationId: currentConversationId, viaUnload: true });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const authUidRef = useRef(auth.currentUser ? auth.currentUser.uid : null);

  // TODO
  //  DONE1. (Optional) Authenticate the agents with a signed URL (make the agent private!!)

  // export type Role = "user" | "ai";
    // export type Mode = "speaking" | "listening";
    // export type Status = "connecting" | "connected" | "disconnecting" | "disconnected";

  const conversation = useRealtimeConversation({
    preferHeadphonesForIosDevices: true,
    micMuted: sharedState.micMuted,
    onConnect: ({ conversationId }) => {
      console.log('Connected to conversation:', conversationId);
      setErrorMessage(''); // Clear error message on successful connection
      setConversationId(conversationId); // Store conversationId in state
      conversationIdRef.current = conversationId; // Keep ref updated
      conversationStartTimeRef.current = Date.now(); // Track when conversation started

      // NOTE: Don't set record: true here - it's already true from proceedWithConversation
      // Setting it again can trigger unnecessary re-renders and potential reconnection loops

      if (chatHistory.length > 0) {
        updateConversationInFirestore(chatHistory).catch(error => {
          console.error('Error updating conversation:', error);
        });
      }
    },
    onDisconnect: () => {
      console.log('Disconnected');

      // Clear connecting flag to allow reconnection
      isConnectingRef.current = false;

      setSharedState(prevState => ({
        ...prevState,
        loading: false,
        talking: false,
        record: false,
      }));
    },
    onMessage: ({ message, source, isFinal }) => {
      console.log('Message from', source, ':', message);

      // Determine the role based on the source and update chat history
      if (source === 'user') {
        addMessageToChatHistory(message, null);
        // ElevenLabs only calls onMessage for finals; Gemini flags
        // intermediates with isFinal: false — only count completed turns.
        if (isFinal !== false) {
          onUserTurnCompleted(chatHistoryRef.current);
        }
      } else if (source === 'ai') {
        addMessageToChatHistory(null, message);
        if (isRealtimeCompletionMessage(message)) {
          reportCompletion();
        }
      }
    },
    onUpdateMessage: ({ message, source }) => {
      console.log('Updating message from', source, ':', message);

      // Update the last message in chat history if source matches
      if (source === 'user') {
        updateLastMessageInChatHistory(message, null);
      } else if (source === 'ai') {
        updateLastMessageInChatHistory(null, message);
      }
    },
    onError: (message, context) => {
      console.error('Error:', message, 'Context:', context);

      // A dead microphone has its own recovery — the raw message would surface untranslated
      // and leave the learner in a session that cannot hear them.
      if (context?.type === 'mic_pipeline_dead') {
        recoverDeadMicrophone(context);
        return;
      }

      if (message !== "Permission dismissed") {
        setErrorMessage(message);
      }
    },
    onDebug: (props) => {
      console.debug('Debug info:', props);
    },
    onModeChange: ({ mode }) => {
      console.log('Mode changed to:', mode);
      //  Mode = "speaking" | "listening";
    },
    onStatusChange: ({ status }) => {
      console.log('Status changed to:', status);
      if (status === 'connected') {
        setErrorMessage(''); // Clear error message on successful connection
        // Only set loading to false when status is actually 'connected'
        setSharedState(prevState => ({
          ...prevState,
          loading: false, // Now user can start talking
        }));

        // Reset circuit breaker after successful stable connection
        // Wait a few seconds to ensure connection is truly stable
        setTimeout(() => {
          if (conversation.status === 'connected') {
            circuitBreaker.reset();
            console.log('✅ Connection stable - circuit breaker reset');
          }
        }, 5000); // 5 second stability check
      } else if (status === 'connecting') {
        // Keep loading true while connecting
        setSharedState(prevState => ({
          ...prevState,
          loading: true,
        }));
      } else if (status === 'disconnected') {
        // Set loading false on disconnect so UI can be interactive again
        setSharedState(prevState => ({
          ...prevState,
          loading: false,
          record: false,
        }));
      }
    },
    onUnhandledClientToolCall: (params) => {
      console.warn('Unhandled client tool call:', params);
    },
  });

  /**
   * The start sequence never produced a usable session. Hand the learner back a screen
   * they can act on instead of a loader that will never finish, and make the event loud
   * enough that we hear about it from monitoring rather than from a support email.
   */
  const recoverStuckRealtimeStart = useCallback((stuckForMs) => {
    console.error(`Realtime start watchdog: no usable session after ${stuckForMs}ms, recovering`);

    // A socket that opened but never went live still holds an agent on the other end,
    // waiting for audio it will never get. Close it rather than leaving it to time out.
    (async () => {
      try {
        if (conversation.status !== 'disconnected') {
          await conversation.endSession();
        }
      } catch (e) {
        console.warn('Realtime start watchdog: endSession failed (safe if already closed)', e);
      }
    })();

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      record: false,
      showStartButton: true,
    }));

    toast(t('activity_start_timeout'));

    const activityId = sharedState.selectedActivity;
    trackRealtimeStartStuckRecovered(activityId, stuckForMs);
    Sentry.captureMessage('Realtime start watchdog recovered a stuck session', {
      level: 'error',
      tags: { function: 'useRealtimeStartWatchdog', activityId },
      extra: { stuckForMs },
    });
  }, [conversation, setSharedState, t, sharedState.selectedActivity]);

  const armStartWatchdog = useRealtimeStartWatchdog({
    loading: sharedState.loading,
    onStuck: recoverStuckRealtimeStart,
  });

  /**
   * The session is live but the microphone never captured anything, so the learner is
   * talking to an agent that cannot hear them. Nothing they do from here will work —
   * end it and say so, rather than letting them keep talking for the fifty-odd seconds
   * until ElevenLabs gives up and hangs up without explanation.
   */
  const recoverDeadMicrophone = useCallback((context) => {
    console.error('Microphone pipeline is dead; ending the session', context);

    (async () => {
      try {
        if (conversation.status !== 'disconnected') {
          await conversation.endSession();
        }
      } catch (e) {
        console.warn('Dead microphone recovery: endSession failed (safe if already closed)', e);
      }
    })();

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      record: false,
      showStartButton: true,
    }));

    toast(t('microphone_not_working'));

    const activityId = sharedState.selectedActivity;
    trackMicrophoneDeadRecovered(activityId, context?.audioContextState);
    Sentry.captureMessage('Microphone pipeline produced no audio', {
      level: 'error',
      tags: { function: 'useMicSilenceWatch', activityId },
      extra: { audioContextState: context?.audioContextState },
    });
  }, [conversation, setSharedState, t, sharedState.selectedActivity]);

  // Kill any active realtime session when the auth state changes (sign out, or
  // switching to a different uid). Without this, the WebSocket / agent stays
  // connected across signOutUser()'s page reload and the next user lands into
  // a session that is still listening from the previous account.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const newUid = user ? user.uid : null;
      const previousUid = authUidRef.current;
      authUidRef.current = newUid;

      const shouldEnd = (newUid === null && previousUid !== null) || (newUid && previousUid && newUid !== previousUid);
      if (!shouldEnd) return;

      try {
        if (conversation.status !== 'disconnected') {
          console.log('🔒 Auth state changed — ending active realtime session');
          await conversation.endSession();
        }
      } catch (e) {
        console.warn('Realtime: endSession on auth change failed (safe to ignore if already disconnected)', e);
      }
    });

    return () => unsubscribe();
  }, [conversation]);

  // Keep track of which languageVariant has been applied to the current realtime session
  const lastAppliedLanguageVariantRef = useRef(sharedState.languageVariant || localStorage.getItem('languageVariant') || 'france');

  /** PROMPT MANAGEMENT */
  const startConversation = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log('⚠️ Connection already in progress, skipping duplicate request');
      return;
    }

    // Circuit breaker: Check if connection attempts are allowed
    if (!circuitBreaker.canAttempt()) {
      console.error('🚫 Circuit breaker OPEN - connection attempt blocked');
      setSharedState(prevState => ({
        ...prevState,
        loading: false,
        record: false,
      }));
      return;
    }

    // Record this connection attempt for circuit breaker tracking
    const attemptAllowed = circuitBreaker.recordAttempt();
    if (!attemptAllowed) {
      console.error('🚫 Circuit breaker TRIGGERED - too many connection attempts');
      setSharedState(prevState => ({
        ...prevState,
        loading: false,
        record: false,
      }));
      return;
    }

    isConnectingRef.current = true;
    console.log('🔄 Starting conversation...');

    setSharedState(prevState => ({
      ...prevState,
      loading: true, // Set loading to true before starting the conversation
    }));

    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: sharedState.echoCancellation,
          noiseSuppression: sharedState.noiseSuppression,
          autoGainControl: sharedState.autoGainControl
        }
      });

      // Get current Language variant
      const currentLanguageVariant = sharedState.languageVariant || localStorage.getItem('languageVariant') || 'france';


       // console.log(sharedState.selectedActivity);
      // console.log(localStorage.getItem('selectedActivity'));

      // Check for custom model and voice from URL parameters (for quick testing)
      const customModel = getCustomModel();
      const voiceName = getVoiceName();

      // Prepare the data for the enhanced agent endpoint
      const requestData = {
        firestoreDocId: getFirestoreDocId(),
        name: getCurrentUserName(),
        level: getLatestLevel() ?? 3, // Default to 3 if user has no level set
        age: sharedState.userAge || '20',
        language: sharedState.userLanguage || 'en',
        userId: getCurrentUserId(),
        activity: sharedState.selectedActivity || localStorage.getItem('selectedActivity'),
        memory: getUserMemory() || undefined,
        resumedChatHistory: chatHistory.length > 0 ? chatHistory : null,
        languageVariant: currentLanguageVariant,
        targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
        useCustomAgent: sharedState.useCustomAgent !== false, // Default to true for custom agent users
        // Partner work context (URL params override stored memory)
        work_domain: getUrlParameter('work_domain') || getUserWork()?.work_domain || undefined,
        work_title: getUrlParameter('work_title') || getUserWork()?.work_title || undefined,
        ...(customModel && { model: customModel }), // Include custom model if provided via ?model= URL parameter
        voice: voiceName // Always include voice (defaults to Zephyr)
      };

      if (customModel) {
        console.log(`🧪 Using custom model from URL parameter: ${customModel}`);
      }

      if (voiceName !== 'Zephyr') {
        console.log(`🎙️ Using custom voice from URL parameter: ${voiceName}`);
      }

      // Single call to enhanced agent/gemini-realtime endpoint that handles everything
      const agentResponse = await axios.post(HTTP_agent, requestData);

      let sessionConfig;

      if (realtimeEngine === 'gemini') {
        // Vertex AI Gemini configuration (prompts generated server-side by proxy)
        const { projectId, model, voice } = agentResponse.data;
        console.log('🤖 Vertex AI Gemini response:', {
          model,
          voice,
          projectId
        });

        // Pass full user context to proxy for server-side prompt generation
        sessionConfig = {
          projectId, // GCP project ID
          model,
          voice, // Voice name for Gemini
          // User context for server-side prompt generation
          firestoreDocId: getFirestoreDocId(),
          name: getCurrentUserName(),
          level: getLatestLevel() ?? 3, // Default to 3 if user has no level set
          age: sharedState.userAge || '20',
          language: sharedState.userLanguage || 'en',
          userId: getCurrentUserId(),
          activity: sharedState.selectedActivity || localStorage.getItem('selectedActivity'),
          memory: getUserMemory() || undefined,
          resumedChatHistory: chatHistory.length > 0 ? chatHistory : null,
          languageVariant: currentLanguageVariant,
          targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
        };
      } else {
        // ElevenLabs configuration (prompts generated server-side by proxy)
        const { signedUrl, agentId: customAgentId } = agentResponse.data;
        console.log('🤖 ElevenLabs response:', { signedUrl: !!signedUrl, customAgentId: customAgentId || 'default' });

        // Pass full user context to proxy for server-side prompt generation
        sessionConfig = {
          signedUrl: signedUrl,
          // User context for server-side prompt generation
          firestoreDocId: getFirestoreDocId(),
          name: getCurrentUserName(),
          level: getLatestLevel() ?? 3, // Default to 3 if user has no level set
          age: sharedState.userAge || '20',
          language: sharedState.userLanguage || 'en',
          userId: getCurrentUserId(),
          activity: sharedState.selectedActivity || localStorage.getItem('selectedActivity'),
          memory: getUserMemory() || undefined,
          resumedChatHistory: chatHistory.length > 0 ? chatHistory : null,
          languageVariant: currentLanguageVariant,
          targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
          // Custom agent settings
          useCustomAgent: sharedState.useCustomAgent !== false,
          customAgentId: customAgentId, // Pass custom agent ID if user has one
          // TTS playback speed (0.7-1.2 for ElevenLabs)
          ttsSpeed: sharedState.ttsSpeed || 1.0,
          // Pass current origin for prompt endpoint (supports deploy previews)
          clientOrigin: window.location.origin,
        };
      }

      const convId = await conversation.startSession(sessionConfig);
      console.log("Started conversation " + convId)
      setConversationId(convId); // Store conversationId in state
      conversationIdRef.current = convId;  // Keep ref updated

      // Clear connecting flag on successful connection
      isConnectingRef.current = false;

    } catch (error) {
      console.error('Failed to start conversation:', error);

      // Clear connecting flag on error
      isConnectingRef.current = false;

      setSharedState(prevState => ({
        ...prevState,
        loading: false,  // Set loading to false on error
      }));

      // Show error to user with safe null checks
      const errorMessage = error?.message || t('fallback_no_audio') || 'Failed to start conversation. Please try again.';
      toast(errorMessage);
    }
  }, [
      // IMPORTANT: DO NOT include 'conversation' here - it changes frequently and causes infinite loops
      // conversation.startSession is stable and doesn't need to be in dependencies
      chatHistory,
      sharedState.userAge,
      sharedState.userLanguage,
      sharedState.selectedActivity,
      sharedState.languageVariant
    ]);

  // Keep ref in sync with the latest startConversation callback
  useEffect(() => {
    startConversationRef.current = startConversation;
  }, [startConversation]);

  // Restart realtime session when languageVariant changes so vocabulary updates immediately
  useEffect(() => {
    const currentVariant = sharedState.languageVariant || localStorage.getItem('languageVariant') || 'france';
    // If session not started yet, just sync ref
    if (!sharedState.firstUserInput) {
      lastAppliedLanguageVariantRef.current = currentVariant;
      return;
    }
    if (lastAppliedLanguageVariantRef.current !== currentVariant) {
      console.log('🇫🇷 Realtime: languageVariant changed, restarting session', {
        from: lastAppliedLanguageVariantRef.current,
        to: currentVariant
      });
      lastAppliedLanguageVariantRef.current = currentVariant;
      (async () => {
        try {
          if (conversation.status !== 'disconnected') {
            await conversation.endSession();
          }
        } catch (e) {
          console.warn('Realtime: endSession error (safe to ignore if already disconnected)', e);
        }
        try {
          await startConversationRef.current();
        } catch (e) {
          console.error('Realtime: failed to restart session after languageVariant change', e);
        }
      })();
    }
  }, [sharedState.languageVariant, sharedState.firstUserInput, conversation.status]);

  // useEffect to watch for changes in isSpeaking
  useEffect(() => {
    handleSpeakingChange(conversation.isSpeaking);
    // Update shared state with speaking status for AGI component
    setSharedState(prevState => ({
      ...prevState,
      conversationIsSpeaking: conversation.isSpeaking
    }));
  }, [conversation.isSpeaking, setSharedState]);

  // useEffect to watch for conversation status changes and control loading state
  useEffect(() => {
    setSharedState(prevState => ({
      ...prevState,
      conversationStatus: conversation.status,
      loading: conversation.status !== 'connected'
    }));
  }, [conversation.status, setSharedState]);

  const finishConversation = useCallback(async (messages, elevenLabsConversationId) => {

    // In realtime, we mostly want to pause the recording and not end the session
    console.log("Finish conversationCalled:" + messages.length + " chatHistroylocal:" + chatHistory.length + " sharedState.chathistory : " + sharedState.chatHistory.length);

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      talking: false,
      lastRecordedBlob: null,
      showActivities: false,  // Show activities when conversation ends
      showStartButton: true // Hide start button when conversation ends
    }));

    // End the realtime session first
    await conversation.endSession();

    const shouldPromptFeedback = messages?.length > 2;
    if (shouldPromptFeedback) {
      lastConversationIdRef.current = getFirestoreDocId();
      lastActivityRef.current = sharedState.selectedActivity;
    }

    // Fire endConversation immediately — /end is guaranteed before any state resets
    useEndConversationRef.current({ resetState: false, conversation: messages, elevenLabsConversationId });

    if (shouldPromptFeedback) {
      setShowConversationFeedbackModal(true);
    }

  }, [conversation]);

  const { endConversation } = useEndConversation();

  const startConversationRef = useRef(startConversation);
  //const toggleMicrophoneRef = useRef(toggleMicrophone);
  const useEndConversationRef = useRef(endConversation);

  const finishConversationRef = useRef(finishConversation);

  useEffect(() => {
    // Only trigger on sharedState.record changes, not on conversation.status changes
    // Status changes are handled by onStatusChange callback
    if (sharedState.record && conversation.status !== 'connected') {
      // Only start conversation if we're not already connected
      startConversationRef.current();
    } else if (!sharedState.record && conversation.status === 'connected') {
      // If we're connected but record is false, just mute the microphone instead of disconnecting
      setSharedState(prevState => ({
        ...prevState,
        micMuted: true,
      }));
    }
    // IMPORTANT: Do NOT include conversation.status in dependencies
    // Including it causes re-render loops because the conversation object is recreated on every render
  }, [sharedState.record]);

  useEffect(() => {

    /** FLAG to stop and reset everything */

    if (sharedState.finishRecording) {
      // Defensive check: Prevent finishing conversation within 2 seconds of starting
      // This prevents accidental triggers from rapid re-renders or UI glitches
      const MIN_CONVERSATION_DURATION_MS = 2000;
      const conversationDuration = conversationStartTimeRef.current
        ? Date.now() - conversationStartTimeRef.current
        : Infinity;

      if (conversationDuration < MIN_CONVERSATION_DURATION_MS) {
        console.warn(`⚠️ Ignoring finishRecording - conversation only lasted ${conversationDuration}ms (minimum ${MIN_CONVERSATION_DURATION_MS}ms)`);
        setSharedState(prevState => ({
          ...prevState,
          finishRecording: false, // Reset the flag without finishing
        }));
        return;
      }

      setSharedState(prevState => ({
        ...prevState,
        finishRecording: false, // Reset the flag
      }));

      finishConversationRef.current(chatHistory, conversationId);
    }
  }, [sharedState.finishRecording, chatHistory, conversationId]);

  /** OTHERs */
  const handleActivitySelect = (activity) => {
    // setSharedState(prevState => ({
    //   ...prevState,
    //   showStartButton: true, // Show start button when activity is selected
    //   showActivities: false // Hide activities when an activity is selected
    // }));
    handleConversationStarted(activity);
  };

  /** CONVERSATION FEEDBACK HANDLERS */
  const handleConversationFeedbackSubmit = (feedbackData) => {
    setShowConversationFeedbackModal(false);
    if (lastConversationIdRef.current) {
      updateFeedbackInFirestore(lastConversationIdRef.current, feedbackData);
      trackFeedbackSubmitted(lastConversationIdRef.current, lastActivityRef.current, feedbackData);
    }
    lastConversationIdRef.current = null;
    lastActivityRef.current = null;
  };

  const handleConversationFeedbackDismiss = () => {
    setShowConversationFeedbackModal(false);
    lastConversationIdRef.current = null;
    lastActivityRef.current = null;
  };

  useEffect(() => {
    const limit = getUrlParameter('limit');

    setSharedState(prevState => ({
      ...prevState,
      timeLimit: limit ? parseInt(limit, 10) : null,
    }));
  }, [setSharedState]);

  useEffect(() => {
    if (errorMessage) {
      toast(errorMessage); // Trigger a toast with the error message
    }
  }, [errorMessage]);

  /** CONVERSATION HISTORY */
  useEffect(() => {
    // console.log("shared chatHistory updated:", sharedState.chatHistory);
    setChatHistory(sharedState.chatHistory);
    // Keep ref updated for navigation scenarios
    chatHistoryRef.current = sharedState.chatHistory;
  }, [sharedState.chatHistory]);

  const addMessageToChatHistory = (userMessage, assistantMessage) => {
    const messagesToAdd = [];

    if (userMessage) {
      const newUserMessage = { role: "user", content: userMessage };
      messagesToAdd.push(newUserMessage);
      trackConversationSendMessage(userMessage, null);
    }

    if (assistantMessage) {
      const newAssistantMessage = { role: "assistant", content: assistantMessage };
      messagesToAdd.push(newAssistantMessage);
      trackConversationSendMessage(null, assistantMessage);
    }

    if (messagesToAdd.length > 0) {
      const nextChatHistory = [...chatHistoryRef.current, ...messagesToAdd];
      chatHistoryRef.current = nextChatHistory;
      setChatHistory(nextChatHistory);

      setSharedState(prevState => ({
        ...prevState,
        chatHistory: nextChatHistory
      }));
    }
  };

  /**
   * Update the last message in chat history (for streaming/tentative messages)
   * This prevents creating multiple message bubbles for partial responses
   */
  const updateLastMessageInChatHistory = (userMessage, assistantMessage) => {
    // Determine which role we're updating
    const targetRole = userMessage ? 'user' : 'assistant';
    const newContent = userMessage || assistantMessage;

    if (!newContent) return;

    const updateHistory = (history = []) => {
      // Find the last message with the matching role
      const updatedHistory = [...history];
      let didUpdate = false;
      for (let i = updatedHistory.length - 1; i >= 0; i--) {
        if (updatedHistory[i].role === targetRole) {
          // Update the content of this message
          updatedHistory[i] = {
            ...updatedHistory[i],
            content: newContent
          };
          didUpdate = true;
          break;
        }
      }

      if (!didUpdate) {
        updatedHistory.push({ role: targetRole, content: newContent });
      }

      return updatedHistory;
    };

    const updatedHistory = updateHistory(chatHistoryRef.current);
    chatHistoryRef.current = updatedHistory;
    setChatHistory(updatedHistory);

    setSharedState(prevState => {
      return {
        ...prevState,
        chatHistory: updatedHistory
      };
    });
  };

  /** INTERFACE */
  const activeLearningPathActivity = getActiveLearningPathActivity();

  return (
    <div>
      {!sharedState.showStartButton &&
        <div className="text-center">
          <ActivityList visible={sharedState.showActivities} onSelect={handleActivitySelect} />
        </div>
      }

      {!sharedState.firstUserInput && sharedState.showStartButton && !sharedState.showActivities &&
        <div className="text-center commencer">
          {(() => {
            const isLite = window.location.pathname === '/lite';
            const hasPlan = currentPlannerData && currentPlannerData.number_activities > 0;
            const targetText = isLite
              ? getTargetLanguageText(sharedState.targetLanguage, 'startButton')
              : getTargetLanguageText(sharedState.targetLanguage, hasPlan ? 'continueYourLearningPlan' : 'startYourLearningPlan');
            const nativeText = isLite
              ? t('commencer')
              : t(hasPlan ? 'continue_your_learning_plan' : 'start_your_learning_plan');
            return (
              <button
                onClick={handleCommencerClick}
                className="start-conversation"
                disabled={isPlannerLoading}
                aria-label={isPlannerLoading ? t('loading') : `${targetText} - ${nativeText}`}
              >
                {isPlannerLoading ? 'Loading...' : targetText}<br />
                {!isLite && <span className="subtitle">{isPlannerLoading ? '' : nativeText}</span>}
              </button>
            );
          })()}
          {/* Hide the activities button in lite mode OR when activity is pre-selected via URL (partner integration) */}
          {window.location.pathname !== '/lite' && !getUrlParameter('activity') && (
            <>
              <button
                className="button activities-button"
                onClick={() => handleConversationStarted(getFreeformStartActivity())}
                aria-label={t('activity_start_conversation')}
              >
                <FontAwesomeIcon icon={faComments} aria-hidden="true" /> {t('activity_start_conversation')}
              </button>
              <button
                className="button activities-button"
                onClick={handleActivities}
                aria-label={t('commencer_select_activity')}
              >
                <FontAwesomeIcon icon={faGamepad} aria-hidden="true" /> {t('commencer_select_activity')}
              </button>
            </>
          )}
          <p className="volume-warning" role="status" aria-live="polite">
            <FontAwesomeIcon icon={faVolumeHigh} aria-hidden="true" /> {t('warning_volume')}
          </p>
          <p className="ai-disclaimer">{t('ai_can_make_mistakes')}</p>
        </div>
      }

      {sharedState.firstUserInput && !sharedState.showActivities &&
        <div>
          {errorMessage && (
            <p className="text-red-500" role="alert" aria-live="assertive">
              {t('status_disconnected')}<br />{errorMessage}
            </p>
          )}
          {activeLearningPathActivity && (
            <ActivitySessionProgressBar
              progressIndex={attemptProgressIndex ?? 0}
              selectedActivity={activeLearningPathActivity}
              conversationProgress={sharedState.conversationProgress}
              activityDoc={activityDocAtStart}
            />
          )}
          <ConversationComponent
            chatHistory={chatHistory}
            sharedState={sharedState}
            setSharedState={setSharedState}
          />
        </div>
      }

      {/* Microphone Permission Modal - Utilise le composant partagé */}
      <MicrophonePermissionModal
        isOpen={showPermissionModal}
        onPermissionGranted={handlePermissionGranted}
        onPermissionDenied={handlePermissionDenied}
      />

      {/* Conversation Feedback Modal - For realtime stop button */}
      <ConversationFeedbackModal
        isOpen={showConversationFeedbackModal}
        onClose={handleConversationFeedbackDismiss}
        onSubmit={handleConversationFeedbackSubmit}
        onSkip={handleConversationFeedbackDismiss}
        conversationProgress={isLearningActivity(lastActivityRef.current) ? sharedState.conversationProgress : null}
      />
    </div>
  );
}

export default Conversation;
